import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Link
} from '@mui/material';
import { Edit, Delete, Add, Link as LinkIcon } from '@mui/icons-material';

const DrivingLicence = () => {
  const [licences] = useState([]);
  const [cities] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentLicence, setCurrentLicence] = useState({
    id: '',
    cityId: '',
    title: '',
    description: '',
    url: '',
    isButtonVisible: true,
    isEnabled: true
  });

  useEffect(() => {
    // Fetch driving licences and cities
    // API calls here
  }, []);

  const handleOpen = (licence = null) => {
    if (licence) {
      setCurrentLicence(licence);
      setEditMode(true);
    } else {
      setCurrentLicence({
        id: '',
        cityId: '',
        title: '',
        description: '',
        url: '',
        isButtonVisible: true,
        isEnabled: true
      });
      setEditMode(false);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditMode(false);
  };

  const handleChange = (e) => {
    setCurrentLicence({
      ...currentLicence,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = () => {
    // API call to save/update driving licence
    handleClose();
  };

  const handleDelete = (cityId) => {
    // API call to delete driving licence
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" color="primary">Driving Licence</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpen()}
          sx={{
            background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
            color: '#ffffff',
            '&:hover': {
              background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
            }
          }}
        >
          Add Driving Licence
        </Button>
      </Box>

      <Grid container spacing={3}>
        {licences.map((licence) => (
          <Grid item xs={12} sm={6} md={4} key={licence.id}>
            <Card 
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  boxShadow: '0 8px 16px rgba(124, 58, 237, 0.1)',
                  transform: 'translateY(-2px)',
                  borderColor: '#328284'
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="h6" gutterBottom>{licence.title}</Typography>
                  <Box>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpen(licence)}
                      sx={{ color: '#0F3D3E' }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDelete(licence.cityId)}
                      sx={{ color: '#ef4444' }}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>

                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 2,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {licence.description}
                </Typography>

                {licence.url && (
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <LinkIcon sx={{ color: '#0F3D3E', fontSize: 20 }} />
                    <Link 
                      href={licence.url} 
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        color: '#0F3D3E',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Visit Website
                    </Link>
                  </Box>
                )}

                <Box display="flex" gap={1}>
                  {licence.isEnabled && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#10b981',
                        bgcolor: '#10b98120',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        display: 'inline-block'
                      }}
                    >
                      Active
                    </Typography>
                  )}
                  {licence.isButtonVisible && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#0F3D3E',
                        bgcolor: '#0F3D3E20',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        display: 'inline-block'
                      }}
                    >
                      Button Visible
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit Driving Licence' : 'Add New Driving Licence'}</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>City</InputLabel>
                  <Select
                    name="cityId"
                    value={currentLicence.cityId}
                    onChange={handleChange}
                    label="City"
                  >
                    {cities.map(city => (
                      <MenuItem key={city.id} value={city.id}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  value={currentLicence.title}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={currentLicence.description}
                  onChange={handleChange}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="URL"
                  name="url"
                  value={currentLicence.url}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" gap={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentLicence.isEnabled}
                        onChange={(e) => handleChange({
                          target: {
                            name: 'isEnabled',
                            value: e.target.checked
                          }
                        })}
                        color="primary"
                      />
                    }
                    label="Enabled"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentLicence.isButtonVisible}
                        onChange={(e) => handleChange({
                          target: {
                            name: 'isButtonVisible',
                            value: e.target.checked
                          }
                        })}
                        color="primary"
                      />
                    }
                    label="Show Button"
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              }
            }}
          >
            {editMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DrivingLicence;
